import Utils from './aria-utils';
import { addClass, hasClass, isHidden } from './dom';

/**
 * @constructor
 * @desc Dialog object providing modal focus management.
 *
 * Assumptions: The element serving as the dialog container is present in the
 * DOM and hidden. The dialog container has role='dialog'.
 *
 * @param dialogId
 *          The ID of the element serving as the dialog container.
 * @param focusAfterClosed
 *          Either the DOM node or the ID of the DOM node to focus when the
 *          dialog closes.
 * @param focusFirst
 *          Optional parameter containing either the DOM node or the ID of the
 *          DOM node to focus when the dialog opens. If not specified, the
 *          first focusable element in the dialog will receive focus.
 */
let aria = {};
aria.openDialogList = aria.openDialogList || new Array(0);
aria.getCurrentDialog = function() {
  if (aria.openDialogList && aria.openDialogList.length) {
    return aria.openDialogList[aria.openDialogList.length - 1];
  }
};

aria.Dialog = function(dialog, focusAfterClosed, focusFirst, options) {
  this.dialogNode = dialog;
  // if (this.dialogNode === null ||
  //   (this.dialogNode.getAttribute('role') !== 'dialog' &&
  //     this.dialogNode.getAttribute('role') !== 'application') &&
  //   this.dialogNode.getAttribute('role') !== 'tooltip'
  // ) {
  //   throw new Error('Dialog() requires a DOM element with ARIA role of dialog.');
  // }
  this.appendTempNode();

  this.options = options || {};
  if (typeof focusAfterClosed === 'string') {
    this.focusAfterClosed = document.getElementById(focusAfterClosed);
  } else if (typeof focusAfterClosed === 'object') {
    this.focusAfterClosed = focusAfterClosed;
  } else {
    this.focusAfterClosed = null;
  }

  if (typeof focusFirst === 'string') {
    this.focusFirst = document.getElementById(focusFirst);
  } else if (typeof focusFirst === 'object') {
    this.focusFirst = focusFirst;
  } else {
    this.focusFirst = null;
  }
  if (this.focusFirst) {
    setTimeout(() => {
      this.focusFirst.focus();
    }, 0);
  } else if (this.focusFirst !== false) {
    Utils.focusFirstDescendant(this.dialogNode, this.preButton);
  }

  if (aria.openDialogList.length > 0) {
    aria.getCurrentDialog().removeListeners();
  }

  this.refresh();

  // this.lastFocus = Utils.findLastFocusEl(dialog);
  this.tabEvent = (e) => {
    if (e.keyCode === Utils.keys.tab) {
      this.trapFocus(e);
    }
  };
  this.addListeners();

  aria.openDialogList.push(this);
  // if (!this.hasReferenceNode()) {
  //   this.createReferenceNode();
  // } else {
  //   // 把占位的button挪到body最后
  //   document.body.appendChild(this.hasReferenceNode());
  // }
};

aria.Dialog.prototype.refresh = function() {
  this.lastFocus = Utils.findLastFocusEl(this.dialogNode, this.nextButton);
  this.firstFocus = Utils.findFirstFocusEl(this.dialogNode, this.preButton);
};

aria.Dialog.prototype.addListeners = function() {
  document.addEventListener('keydown', this.tabEvent, true);
};

aria.Dialog.prototype.removeListeners = function() {
  document.removeEventListener('keydown', this.tabEvent, true);
};

aria.Dialog.prototype.closeDialog = function(loseFocusAfterClosed) {
  aria.openDialogList.pop();
  this.removeListeners();
  if (this.focusAfterClosed) {
    setTimeout(() => {
      if (isHidden(this.focusAfterClosed) || this.focusAfterClosed === document.body) {
        loseFocusAfterClosed && loseFocusAfterClosed();
      } else {
        this.focusAfterClosed.focus();
      }
    });
  }
  if (aria.openDialogList.length) {
    aria.getCurrentDialog().addListeners();
  }
  this.removeTempNode();
  // else {
  //   this.removeReferenceNode();
  // }
};
aria.Dialog.prototype.setFocusEl = function() {
  if (this.focusFirst) {
    this.focusFirst.focus();
  } else {
    Utils.focusFirstDescendant(this.dialogNode, this.preButton);
  }
};

aria.Dialog.prototype.trapFocus = function(event) {
  const dialogNode = aria.getCurrentDialog().dialogNode;
  // always evaluate the latest DOM, because dialogNode is not stable
  const firstFocusEl = Utils.findFirstFocusEl(this.dialogNode, this.preButton);
  const lastFocusEl = Utils.findLastFocusEl(this.dialogNode, this.nextButton);
  if (event.shiftKey) {
    const firstFocusMatch = firstFocusEl === event.target || firstFocusEl.contains(event.target);
    // Need to get the current focus event at the next tick time
    setTimeout(() => {
      const canFocusChildren = Utils.getFocusableChildElements(dialogNode) || [];
      const prevCanFocus = canFocusChildren.findIndex(el => el === document.activeElement) > 0;
      if (firstFocusMatch || !prevCanFocus) {
        Utils.focusLastDescendant(dialogNode, this.nextButton);
      }
    }, 0);
  } else if (
    lastFocusEl === event.target ||
    lastFocusEl.contains(event.target)
  ) {
    setTimeout(() => {
      Utils.focusFirstDescendant(dialogNode, this.preButton);
    }, 0);
  }
};

aria.Dialog.prototype.focusNext = function() {
  return Utils.focusNext(this.dialogNode, null, this.nextButton);
};

aria.Dialog.prototype.focusPrevious = function() {
  return Utils.focusPrevious(this.dialogNode, null, this.preButton);
};

aria.Dialog.prototype.focusFirstDescendant = function() {
  return Utils.focusFirstDescendant(this.dialogNode, this.preButton);
};

aria.Dialog.prototype.focusLastDescendant = function() {
  return Utils.focusLastDescendant(this.dialogNode, this.nextButton);
};
// aria.Dialog.prototype.hasReferenceNode = function() {
//   return document.getElementById('tempNodeForAriaDialog');
// };
// aria.Dialog.prototype.createReferenceNode = function() {
//   let button = document.createElement('button');
//   button.setAttribute('id', 'tempNodeForAriaDialog');
//   button.innerHTML = 'Placeholder button for aria dialog';
//   document.body.appendChild(button);
// };
// aria.Dialog.prototype.removeReferenceNode = function() {
//   const button = document.getElementById('tempNodeForAriaDialog');
//   button && document.body.removeChild(button);
// };

aria.Dialog.prototype.appendTempNode = function() {
  const dialog = this.dialogNode;
  if (hasClass(dialog.children[0], 'dialog-temp-node')) {
    return;
  }
  const preButton = this.preButton = createButton();
  dialog.insertBefore(preButton, dialog.children[0]);
  const nextButton = this.nextButton = createButton();
  dialog.appendChild(nextButton);
};

aria.Dialog.prototype.removeTempNode = function() {
  const dialog = this.dialogNode;
  this.preButton && dialog.removeChild(this.preButton);
  this.nextButton && dialog.removeChild(this.nextButton);
};

function createButton() {
  const button = document.createElement('button');
  addClass(button, 'dialog-temp-node');
  button.setAttribute('aria-hidden', 'true');
  button.innerText = 'Aria Button';
  return button;
};

export default aria.Dialog;
